import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useApi from "../../hooks/use-api";
import api from "../../api/api";
import Sidebar from "../../components/Sidebar/Sidebar";
import { SidebarItemProps } from "../../components/Sidebar/SidebarItem";
import AccountTopBar from "../../components/AccountTopBar/AccountTopBar";
import { Tags, Tag, UserType } from "../../utils/props";
import { documentSections, documentTagNames } from "../../utils/documentSections";
import { buildAuthenticatedFileUrl } from "../../utils/utils";

export default function UserDocuments() {
  const { id } = useParams<{ id: string }>();
  const adminFirstName = localStorage.getItem('first_name')?.replace(/['"]+/g, '') ?? '';
  const adminLastName = localStorage.getItem('last_name')?.replace(/['"]+/g, '') ?? '';
  const navigate = useNavigate();
  const [userProfileTag, setUserProfileTag] = useState<number | undefined>(undefined);
  const [tagIds, setTagIds] = useState<Record<string, number>>({});
  const [uploadedFiles, setUploadedFiles] = useState<Record<number, Array<{ id: number; name: string, url: string, expirationDate: string, notes: string, approved: boolean }>>>({});
  const [editingFileId, setEditingFileId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<{ expirationDate: string, notes: string }>({ expirationDate: '', notes: '' });
  const [approvingFileId, setApprovingFileId] = useState<number | null>(null);
  const [approveError, setApproveError] = useState<string | undefined>(undefined);

  const getFileDisplayName = (value: string) => {
    const rawValue = (value || '').trim();
    if (!rawValue) return '';

    try {
      if (/^https?:\/\//i.test(rawValue)) {
        const parsed = new URL(rawValue);
        const segments = parsed.pathname.split('/').filter(Boolean);
        return segments[segments.length - 1] || rawValue;
      }
    } catch {
      // Fall back to path split below.
    }

    const normalized = rawValue.replace(/\\/g, '/');
    const segments = normalized.split('/').filter(Boolean);
    return segments[segments.length - 1] || rawValue;
  }

  // Only allow admins
  useEffect(() => {
    const type = localStorage.getItem("type");
    if (type !== UserType.Admin.toString()) {
      navigate("/personal-information");
    }
  }, [navigate]);

  const getUser = useApi({
    path: `/company/${id}`,
  });

  const tags = useApi({
    path: '/tags',
    callback: () => {
      // Tags are loaded with explicit pagination in loadAllCompanyTags.
    }
  });

  const buildTagState = (allTags: Tag[]) => {
    const foundTagIds: Record<string, number> = {};
    const tagNames: string[] = [String(Tags.ProfilePicture), ...documentTagNames];

    tagNames.forEach((tagName) => {
      const found = allTags.find((item) => item.name === tagName && item.deleted !== 1);
      if (found) {
        foundTagIds[tagName] = found.id;
      }
    });

    return foundTagIds;
  };

  const loadAllCompanyTags = async (companyId: string) => {
    const allTags: Tag[] = [];
    let currentPage = 1;
    let totalPages = 1;

    while (currentPage <= totalPages) {
      const response = await tags.get(
        `?company_id=${companyId}&current_page=${currentPage}&items_per_page=500`,
      );
      const payload = response?.data;
      const items: Tag[] = payload?.items ?? [];
      const nextTotalPages = Number(payload?.total_pages ?? 1);

      allTags.push(...items);
      totalPages = Number.isNaN(nextTotalPages) ? 1 : nextTotalPages;
      currentPage += 1;
    }

    const resolvedTagState = buildTagState(allTags);
    setTagIds(resolvedTagState);
    setUserProfileTag(resolvedTagState[String(Tags.ProfilePicture)]);
  };

  const fetchFilesApi = useApi({
    path: '',
    callback: (data, context) => {
      const tagId = context?.tagId;
      if (data && data.items && tagId) {
        setUploadedFiles(prev => ({
          ...prev,
          [tagId]: data.items.map((file: any) => ({
            id: file.id,
            name: getFileDisplayName(file.title || file.filename),
            url: file.filename,
            expirationDate: file.expiration_date || '',
            notes: file.notes || '',
            approved: !!file.approved,
          })),
        }));
      }
    }
  });

  const updateFileApi = useApi({
    path: '/file',
    defaultErrorMessage: 'File update failed',
  });

  useEffect(() => {
    if (id) getUser.get();
    if (id) {
      loadAllCompanyTags(id);
    }
  }, [id]);

  useEffect(() => {
    if (Object.keys(tagIds).length > 0) {
      Object.values(tagIds).forEach(tagId => {
        fetchFilesApi.get(`/files?tag_id=${tagId}&company_id=${id}`, { tagId });
      });
    }
  }, [tagIds, id]);

  const onStartEditFile = (file: { id: number; expirationDate: string; notes: string }) => {
    setEditingFileId(file.id);
    setEditValues({ expirationDate: file.expirationDate, notes: file.notes });
  }

  const onCancelEditFile = () => {
    setEditingFileId(null);
    setEditValues({ expirationDate: '', notes: '' });
  }

  const onSaveFile = (fileId: number, tag: number | undefined) => {
    if (typeof tag !== 'number') {
      return;
    }

    updateFileApi.patch(fileId, {
      expiration_date: editValues.expirationDate,
      notes: editValues.notes,
    }).then(() => {
      setEditingFileId(null);
      fetchFilesApi.get(`/files?tag_id=${tag}&company_id=${id}`, { tagId: tag });
    });
  }

  const onApproveFile = async (fileId: number, tag: number | undefined) => {
    if (typeof tag !== 'number') {
      return;
    }

    setApprovingFileId(fileId);
    setApproveError(undefined);

    const storedToken = localStorage.getItem('token');
    const headers = storedToken ? { Authorization: `Bearer ${JSON.parse(storedToken)}` } : {};

    try {
      await api.post(`/file/${fileId}/approve`, {}, { headers });
      fetchFilesApi.get(`/files?tag_id=${tag}&company_id=${id}`, { tagId: tag });
    } catch {
      setApproveError('Failed to approve file. Please try again.');
    } finally {
      setApprovingFileId(null);
    }
  }

  const sidebarItems: SidebarItemProps[] = [
    { label: 'User Information', href: `/user-information/${id}` },
    ...documentSections.map((section) => ({
      label: section.label,
      href: `#${section.id}`,
    })),
  ];

  return (
    <>
      <AccountTopBar showNav={false} />
      <div className="pd:mx-0 min-h-screen w-full bg-white px-5 md:flex lg:px-[190px]">
      <div className="flex-shrink-0 bg-white lg:min-w-[260px]">
        <Sidebar
          userName={`${adminFirstName} ${adminLastName}`}
          items={sidebarItems}
          tagId={userProfileTag ?? 0}
        />
      </div>
      <div className="flex flex-1 flex-col items-start py-12 md:ml-16">
        <div className="w-full">
          <h1 id="user-documents" className="mb-1 text-2xl font-bold">User Documents</h1>
          {documentSections.map((section) => {
            return (
              <div key={section.id} className="flex flex-col">
                <h2 id={section.id} className="mt-5 text-xl font-bold">{section.label}</h2>
                <div className="flex w-full flex-col border-b border-gray-200 py-4">
                  {section.documents.map((document) => {
                    const documentTagId = tagIds[document.tagName];
                    const documentFiles =
                      typeof documentTagId === 'number' ? uploadedFiles[documentTagId] ?? [] : [];

                    return (
                      <div
                        key={document.id}
                        className="grid w-full gap-4 border-b border-gray-100 py-4 md:grid-cols-[minmax(260px,1fr)_minmax(0,2fr)]"
                      >
                        <div className="flex flex-col">
                          <h3 className="text-lg font-semibold text-gray-900">{document.label}</h3>
                        </div>
                        <div className="w-full">
                          <ul className="w-full rounded border border-gray-200 bg-[#fafbfc] p-3">
                            {documentFiles.length === 0 ? (
                              <li className="text-sm text-gray-500">No files uploaded</li>
                            ) : (
                              documentFiles.map((file) => {
                                const isEditing = editingFileId === file.id;
                                const isApproving = approvingFileId === file.id;

                                return (
                                  <li key={file.id} className="mb-2 flex flex-col gap-2 rounded border border-gray-100 bg-white p-2 text-sm text-gray-800">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <a href={buildAuthenticatedFileUrl(file.url)} target="_blank" rel="noopener noreferrer" className="underline break-all">
                                          {file.name}
                                        </a>
                                        {file.approved ? (
                                          <span className="rounded-full border border-green-300 bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700">
                                            Approved
                                          </span>
                                        ) : (
                                          <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                                            Pending approval
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex shrink-0 flex-wrap gap-2">
                                        {isEditing ? (
                                          <>
                                            <button
                                              type="button"
                                              className="rounded border border-green-300 px-2 py-1 text-xs font-semibold text-green-700 hover:bg-green-50"
                                              onClick={() => onSaveFile(file.id, documentTagId)}
                                            >
                                              Save
                                            </button>
                                            <button
                                              type="button"
                                              className="rounded border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                                              onClick={onCancelEditFile}
                                            >
                                              Cancel
                                            </button>
                                          </>
                                        ) : (
                                          <button
                                            type="button"
                                            className="rounded border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                                            onClick={() => onStartEditFile(file)}
                                          >
                                            Edit
                                          </button>
                                        )}
                                        {!file.approved && (
                                          <button
                                            type="button"
                                            className="rounded border border-green-300 px-2 py-1 text-xs font-semibold text-green-700 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-60"
                                            onClick={() => onApproveFile(file.id, documentTagId)}
                                            disabled={isApproving}
                                          >
                                            {isApproving ? 'Approving…' : 'Approve'}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    {isEditing ? (
                                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                        <label className="flex items-center gap-2">
                                          <span className="text-xs font-medium text-gray-600">Expiration date</span>
                                          <input
                                            type="date"
                                            className="rounded border border-gray-300 px-2 py-1 text-xs"
                                            value={editValues.expirationDate}
                                            onChange={(e) => setEditValues((prev) => ({ ...prev, expirationDate: e.target.value }))}
                                          />
                                        </label>
                                        <label className="flex flex-1 items-center gap-2">
                                          <span className="text-xs font-medium text-gray-600">Notes</span>
                                          <input
                                            type="text"
                                            className="w-full flex-1 rounded border border-gray-300 px-2 py-1 text-xs"
                                            value={editValues.notes}
                                            onChange={(e) => setEditValues((prev) => ({ ...prev, notes: e.target.value }))}
                                          />
                                        </label>
                                      </div>
                                    ) : (
                                      (file.expirationDate || file.notes) && (
                                        <div className="flex flex-col gap-0.5 text-xs text-gray-500 sm:flex-row sm:gap-4">
                                          {file.expirationDate && <span>Expires: {file.expirationDate}</span>}
                                          {file.notes && <span>Notes: {file.notes}</span>}
                                        </div>
                                      )
                                    )}
                                  </li>
                                );
                              })
                            )}
                          </ul>
                          {approveError && (
                            <p className="mt-2 text-xs text-red-600">{approveError}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      </div>
    </>
  );
}